<template lang="pug">
  .range-input(:class="{'--active': isActive}")
    .range-input__body(@click="onClick")
      .range-input__title
        | {{ placeholder }}
      .range-input__arrow
        img(:src="require('~/assets/img/icons/filter-arrow.svg')" alt="arrow")

      template(v-if="isActive")
        .range-input__inputs
          .range-input__input(class="--from")
            input(ref="from" v-model="from" type="number" :placeholder="$t('from')" min="0" :max="to")
            transition(name="fade" duration="200")
              .range-input__input-clear(v-if="from > 0" @click="onFromClearClick()")
                img(:src="require('~/assets/img/icons/close.svg')" alt="close")
          span.range-input__separator —
          .range-input__input(class="--to")
            input(v-model="to" type="number" :placeholder="$t('to')" :min="from || 0")
            transition(name="fade" duration="100")
              .range-input__input-clear(v-if="to > 0" @click="onToClearClick()")
                img(:src="require('~/assets/img/icons/close.svg')" alt="close")

    .range-input__currencies(v-if="currencies.length")
      .range-input__currency(
        v-for="curr in currencies"
        :class="[`--${curr}`, { '--active': activeCurrencies.includes(curr) }]"
        @click="toggleCurrency(curr)"
      )
        img(v-if="curr === 'coins'" :src="require('~/assets/img/icons/coins.svg')" alt="coins")
        img(v-if="curr === 'money'" :src="require('~/assets/img/icons/v.svg')" alt="money")

</template>

<script>
import { debounce } from 'throttle-debounce'

export default {
  name: 'RangeInput',

  props: {
    placeholder: {
      type: String,
      default: ''
    },

    value: {
      type: Object,
      default: () => ({
        to: null,
        from: null,
        activeCurrencies: []
      })
    },

    currencies: {
      type: Array,
      default: () => []
    }
  },

  data ({
    value: { from = null, to = null, activeCurrencies = [] },
    currencies
  }) {
    if (currencies?.length === 1) {
      activeCurrencies = currencies
    }

    return {
      isActive: from || to,
      from,
      to,
      activeCurrencies
    }
  },

  watch: {
    from () {
      this.onChangeWithDebunce()
    },

    to () {
      this.onChangeWithDebunce()
    }
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onClickOutside)
  },

  mounted () {
    this.onChangeWithDebunce = debounce(1000, this.onChange)
    document.addEventListener('click', this.onClickOutside)
  },

  methods: {
    onClick () {
      if (!this.isActive) {
        this.isActive = !this.isActive

        this.$nextTick(() => {
          this.$refs.from.focus()
        })
      }
    },

    onClickOutside ({ target }) {
      if (!this.$el.contains(target)) {
        this.isActive = this.from || this.to
      }
    },

    onChange () {
      if (
        this.withCurrency &&
        (this.from || this.to) &&
        !this.activeCurrencies.length
      ) {
        this.activeCurrencies.push(this.currencies[0])
      }

      this.$emit('input', {
        from: this.from,
        to: this.to,
        activeCurrencies: this.activeCurrencies
      })
    },

    onFromClearClick () {
      this.from = null
    },

    onToClearClick () {
      this.to = null
    },

    toggleCurrency (curr) {
      if (this.activeCurrencies.includes(curr)) {
        this.activeCurrencies = this.activeCurrencies.filter((item) => {
          if (curr === 'money') {
            return item !== 'money' && item !== 'tickets'
          }

          return item !== curr
        })
      } else {
        this.activeCurrencies = [curr]

        if (curr === 'money') {
          this.activeCurrencies.push('tickets')
        }
      }

      this.onChangeWithDebunce()
    }
  }
}
</script>

<style lang="scss">
.range-input {
  @include text-display100;

  align-items: center;
  color: $white;
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: 0 0 0 16px;
  border-radius: 8px;
  background: $blue-grey60;
  cursor: pointer;
  transition: background 0.2s $easing;
  height: 56px;

  &:hover {
    background: $blue-grey40;
  }

  //.--active & {
  //  background: $gradient-primary;
  //}

  &__body {
    min-width: 190px;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
  }

  &__currencies {
    display: flex;
    height: 100%;
    padding: 0 8px;
    align-items: center;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      height: 40px;
      width: 0;
      border-left: 1px solid $blue-grey30;
    }
  }

  &__currency {
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s $easing;
    border-radius: 8px;

    img {
      width: 24px;
      height: 24px;
    }

    svg path {
      fill: $blue-grey10;
      transition: fill 0.2s $easing;
    }

    &.--active.--coins {
      background: $amber60-20;
    }

    &.--active.--money {
      background: $green60-20;
    }

    /* stylelint-disable-next-line */
    &.--active.--coins svg,
    &.--coins svg:hover {
      /* stylelint-disable-next-line */
      path {
        fill: $amber60;
      }
    }

    /* stylelint-disable-next-line */
    &.--active.--money svg,
    &.--money svg:hover {
      /* stylelint-disable-next-line */
      path {
        fill: $green60;
      }
    }

    &:not(:last-child) {
      margin-right: 4px;
    }
  }

  &__title {
    transition: all 0.2s $easing;
    position: absolute;
    height: 18px;
    top: 50%;
    margin-top: -9px;
    font-weight: 500;

    .--active & {
      font-size: 14px;
      color: $primary05;
      left: 0;
      top: 6px;
      white-space: nowrap;
      height: 14px;
      line-height: 14px;
      margin-top: 0;
    }
  }

  &__inputs {
    height: 24px;
    display: flex;
    align-items: center;
    padding-top: 10px;
  }

  &__separator {
    width: 15px;
    margin-right: 4px;
    margin-top: -4px;
  }

  &__input {
    position: relative;
    height: 24px;

    input {
      @include text-display100;

      width: 80px;
      padding: 0 24px 0 0;
      border: none;
      background: transparent;
      color: white;

      &::placeholder {
        @include text-display100;

        color: white;
      }
    }

    &-clear {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      right: 0;
      top: 0;

      svg rect {
        fill: $blue-grey05;
      }
    }
  }

  &__arrow {
    position: absolute;
    right: 8px;
    top: 50%;
    margin-top: -10px;
    height: 20px;
    width: 20px;
    display: flex;

    .--active & {
      display: none;
    }
  }
}
</style>
